import React, { FC, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";

import { Button, IButtonProps } from "@/components/Button";
import { Header } from "@/components/Header";
import { Text } from "@/components/Text";
import { ConsultantProfileContext, UserContext } from "@/components/User";
import { fetchConsultantProfile, IConsultantProfile, TCompletenessItemKey } from "@/data/consultant-profiles";
import { resetAssessments } from "@/data/users";
import { Flex, Img, inDialogModal, IOpenable } from "@/eworkitComponents";
import { CloseButton } from "@/views/components/CloseButton";
import { ModalButton, StyledModal } from "@/views/components/Modal";
import { useToasts } from "@/views/components/Toast/useToasts";

import resetAssessmentsSvg from "./assets/reset-assessments.svg";

interface IResetAssessmentsModalProps {
  assessments: TCompletenessItemKey[];
  onUpdate: (profile: IConsultantProfile) => void;
}

const ResetAssessmentsModalComponent: FC<IResetAssessmentsModalProps & IOpenable> = ({
  assessments,
  onClose,
  onUpdate,
}) => {
  const { addToast } = useToasts();
  const User = useContext(UserContext);
  const ConsultantProfile = useContext(ConsultantProfileContext);
  const { t } = useTranslation(["session", "common"]);

  const companyId = User.contextCompany && User.contextCompany.id;

  const handleResetClick = useCallback(async () => {
    try {
      await resetAssessments({ companyId }, { assessments });
      addToast({
        text: t(
          assessments.length > 1
            ? "CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.RESET.SUCCESS_MESSAGE_MULTI"
            : "CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.RESET.SUCCESS_MESSAGE",
        ),
        type: "success",
      });

      const newProfile = await fetchConsultantProfile({ consultantProfileId: ConsultantProfile.currentProfileId });
      onUpdate(newProfile);

      onClose();
    } catch (error) {
      addToast({
        text: t("common:VALIDATION_MESSAGES.GENERAL"),
        type: "warning",
      });
    }
  }, [assessments, addToast, companyId]);

  const assessmentsString = assessments
    .map(a => t(`CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.${a}.LONG_TITLE`))
    .join(" & ");

  return (
    <StyledModal>
      <Flex direction="column" padding={["lg"]} innerMargin="lg" justify="center">
        <Flex justify="space-between">
          <Header weight="bold">{t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.RESET.HEADER")}</Header>
          <CloseButton onClick={onClose} testName="close" />
        </Flex>

        <Flex align="center" justify="center">
          <Img src={resetAssessmentsSvg} />
        </Flex>

        <Text size="md" color="black">
          {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.RESET.TEXT", {
            value: assessmentsString,
            interpolation: { escapeValue: false },
          })}
        </Text>

        <Flex direction="row" justify="flex-end" innerMargin="md">
          <Button size="lg" variant="secondary" onClick={onClose}>
            {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.RESET.BUTTON_CANCEL")}
          </Button>
          <Button size="lg" variant="primary" onClick={handleResetClick}>
            {t("CONSULTANT_PROFILES.COMPLETENESS.CONTENT.ASSESSMENT.RESET.BUTTON_RESET_RESULTS")}
          </Button>
        </Flex>
      </Flex>
    </StyledModal>
  );
};

export const ResetAssessmentsDialogModal = inDialogModal(ResetAssessmentsModalComponent);

interface IResetAssessmentsModalButtonProps extends Omit<IButtonProps, "ref"> {
  modalProps: IResetAssessmentsModalProps;
}

export const ResetAssessmentsModalButton: FC<IResetAssessmentsModalButtonProps> = ({ modalProps, ...buttonProps }) => {
  return (
    <ModalButton
      openable={ResetAssessmentsDialogModal}
      openableProps={{ ...modalProps, size: "xs" }}
      {...buttonProps}
    />
  );
};
